/**
 * PwaLauncherPage — https://pwa.hustlesuite.au/
 *
 * A native-app-drawer style launcher for every module in the Suite.
 * Renders when the hostname is `pwa.hustlesuite.au` (or the SPA visits
 * `/pwa`).
 *
 * Layout (iPhone-inspired):
 *   • Scrollable grid of every module → each tile deep-links to
 *     `app.<domain>` for that module
 *   • Pinned Settings tile in the grid opens the member area
 *   • **Bottom dock** — 4 user-selected favourite modules, always
 *     visible, persisted in localStorage (hp_pwa_dock_v1). Tap "Edit"
 *     to reorder / swap favourites.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Settings as SettingsIcon, Search, Pencil, Check, X, Plus, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LAUNCHER_TILES, recordServerOpen, fetchServerRecent } from "@/lib/moduleLauncher";
import "./PwaLauncherPage.css";

const DOCK_KEY = "hp_pwa_dock_v1";
const DOCK_DEFAULT = ["settings", "eats", "shop", "meetups"];
const DOCK_MAX = 4;
const RECENT_KEY = "hp_pwa_recent_v1";
const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // last 7 days
const RECENT_MAX = 8;

function loadRecent(allKeys) {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - RECENT_WINDOW_MS;
    return parsed
      .filter((e) => e && allKeys.has(e.key) && (e.ts || 0) >= cutoff)
      .slice(0, RECENT_MAX);
  } catch { return []; }
}

function recordOpen(key) {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const arr = Array.isArray(list) ? list.filter((e) => e && e.key !== key) : [];
    arr.unshift({ key, ts: Date.now() });
    localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, 24)));
  } catch { /* ignore */ }
}

function loadDock(allKeys) {
  if (typeof window === "undefined") return DOCK_DEFAULT.slice();
  try {
    const raw = localStorage.getItem(DOCK_KEY);
    if (!raw) return DOCK_DEFAULT.slice();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DOCK_DEFAULT.slice();
    const clean = parsed.filter((k) => allKeys.has(k)).slice(0, DOCK_MAX);
    return clean.length ? clean : DOCK_DEFAULT.slice();
  } catch {
    return DOCK_DEFAULT.slice();
  }
}

function saveDock(keys) {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(DOCK_KEY, JSON.stringify(keys)); } catch { /* ignore */ }
}

function TileIcon({ tile, size }) {
  const [errored, setErrored] = useState(false);
  const bg = tile.color || "#1e293b";
  const style = { backgroundColor: bg };
  const iconPx = size === "dock" ? 30 : 28;
  if (tile.key === "settings") {
    return (
      <div className="pwa-tile__icon" style={style} data-testid={`pwa-tile-icon-${tile.key}`}>
        <SettingsIcon size={iconPx} strokeWidth={2.2} color="#fff" />
      </div>
    );
  }
  if (!tile.icon192 || errored) {
    return (
      <div className="pwa-tile__icon" style={style} data-testid={`pwa-tile-icon-${tile.key}`}>
        {tile.initials || tile.label?.[0] || "•"}
      </div>
    );
  }
  return (
    <div className="pwa-tile__icon" style={style} data-testid={`pwa-tile-icon-${tile.key}`}>
      <img
        src={tile.icon192}
        alt={tile.title}
        onError={() => setErrored(true)}
        loading="lazy"
      />
    </div>
  );
}

function Tile({ tile, editing, onRemove, onAdd, inDock, dockFull, onClickCapture }) {
  const remove = (e) => { e.preventDefault(); e.stopPropagation(); onRemove?.(tile.key); };
  const add    = (e) => { e.preventDefault(); e.stopPropagation(); onAdd?.(tile.key); };
  const showAddBadge = editing && !inDock && !dockFull;
  const showRemoveBadge = editing && inDock;

  const inner = (
    <>
      <TileIcon tile={tile} />
      <div className="pwa-tile__label">{tile.label || tile.title}</div>
      {showRemoveBadge && (
        <button
          type="button"
          className="pwa-tile__badge pwa-tile__badge--remove"
          onClick={remove}
          aria-label={`Remove ${tile.title} from dock`}
          data-testid={`pwa-tile-remove-${tile.key}`}
        >
          <X size={12} strokeWidth={3} />
        </button>
      )}
      {showAddBadge && (
        <button
          type="button"
          className="pwa-tile__badge pwa-tile__badge--add"
          onClick={add}
          aria-label={`Pin ${tile.title} to dock`}
          data-testid={`pwa-tile-add-${tile.key}`}
        >
          <Plus size={12} strokeWidth={3} />
        </button>
      )}
    </>
  );

  const className = `pwa-tile ${editing ? "pwa-tile--editing" : ""}`;
  const onClickWhileEditing = (e) => {
    if (editing) {
      e.preventDefault();
      if (inDock) onRemove?.(tile.key);
      else if (!dockFull) onAdd?.(tile.key);
      return;
    }
    // Track opens (Recently Used row).
    onClickCapture?.(tile.key);
  };

  if (tile.external) {
    return (
      <a
        className={className}
        href={editing ? undefined : tile.href}
        onClick={onClickWhileEditing}
        data-testid={`pwa-tile-${tile.key}`}
        aria-label={tile.title}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      className={className}
      to={editing ? "#" : tile.href}
      onClick={onClickWhileEditing}
      data-testid={`pwa-tile-${tile.key}`}
      aria-label={tile.title}
    >
      {inner}
    </Link>
  );
}

export default function PwaLauncherPage() {
  const { user, checking } = useAuth();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(false);

  // Update the browser tab title on this launcher (the shared SPA
  // index.html ships with "HustlePlaza" for legacy reasons).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.title;
    document.title = "HustleSuite — All your apps";
    document.body.setAttribute("data-pwa-launcher", "1");
    return () => {
      document.title = prev;
      document.body.removeAttribute("data-pwa-launcher");
    };
  }, []);

  const tilesByKey = useMemo(() => {
    const m = {};
    LAUNCHER_TILES.forEach((t) => { m[t.key] = t; });
    return m;
  }, []);
  const allKeys = useMemo(
    () => new Set(LAUNCHER_TILES.map((t) => t.key)),
    []
  );

  const [dock, setDock] = useState(() => loadDock(allKeys));
  useEffect(() => { saveDock(dock); }, [dock]);

  const [recent, setRecent] = useState(() => loadRecent(allKeys));

  // On mount (and whenever auth state flips), pull server-side recents
  // and MERGE with the local list — backend wins on ties. This gives
  // cross-device Recently-Used sync for signed-in users.
  useEffect(() => {
    if (checking || !user) return;
    let cancelled = false;
    fetchServerRecent(RECENT_MAX * 3).then((serverList) => {
      if (cancelled || !serverList.length) return;
      try {
        const raw = localStorage.getItem(RECENT_KEY);
        const local = raw ? JSON.parse(raw) : [];
        const map = new Map();
        (Array.isArray(local) ? local : []).forEach((e) => {
          if (e && e.key) map.set(e.key, e.ts || 0);
        });
        serverList.forEach((e) => {
          const prev = map.get(e.key) || 0;
          if (e.ts >= prev) map.set(e.key, e.ts);
        });
        const merged = Array.from(map, ([key, ts]) => ({ key, ts }))
          .sort((a, b) => b.ts - a.ts)
          .slice(0, 24);
        localStorage.setItem(RECENT_KEY, JSON.stringify(merged));
        setRecent(loadRecent(allKeys));
      } catch { /* ignore */ }
    });
    return () => { cancelled = true; };
  }, [checking, user, allKeys]);

  const trackOpen = useCallback((key) => {
    recordOpen(key);
    setRecent(loadRecent(allKeys));
    // Fire-and-forget backend sync (silent on 401 for anon users).
    recordServerOpen(key);
  }, [allKeys]);

  const dockTiles = useMemo(
    () => dock.map((k) => tilesByKey[k]).filter(Boolean),
    [dock, tilesByKey]
  );
  const dockSet = useMemo(() => new Set(dock), [dock]);

  const recentTiles = useMemo(
    () => recent.map((e) => tilesByKey[e.key])
                .filter((t) => t && !dockSet.has(t.key))
                .slice(0, RECENT_MAX),
    [recent, dockSet, tilesByKey]
  );

  const gridTiles = useMemo(() => {
    // Everything except what's already docked (iOS style — dock apps
    // hide from the main pages).
    const base = LAUNCHER_TILES.filter((t) => !dockSet.has(t.key));
    const query = q.trim().toLowerCase();
    if (!query) return base;
    return base.filter((t) =>
      (t.title || "").toLowerCase().includes(query)
      || (t.label || "").toLowerCase().includes(query)
      || (t.desc  || "").toLowerCase().includes(query)
      || (t.key   || "").toLowerCase().includes(query)
    );
  }, [dockSet, q]);

  const addToDock = useCallback((key) => {
    setDock((prev) => {
      if (prev.includes(key) || prev.length >= DOCK_MAX) return prev;
      return [...prev, key];
    });
  }, []);
  const removeFromDock = useCallback((key) => {
    setDock((prev) => prev.filter((k) => k !== key));
  }, []);
  const resetDock = useCallback(() => setDock(DOCK_DEFAULT.slice()), []);

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  const dockFull = dock.length >= DOCK_MAX;

  return (
    <div className={`pwa-launcher ${editing ? "pwa-launcher--editing" : ""}`} data-testid="pwa-launcher">
      <div className="pwa-launcher__inner">
        <header className="pwa-launcher__header">
          <div className="pwa-launcher__brand">
            <div className="pwa-launcher__brand-mark">H</div>
            <div className="pwa-launcher__brand-text">
              <div className="title">HustleSuite</div>
              <div className="subtitle">Your one-tap app drawer</div>
            </div>
          </div>
          <div className="pwa-launcher__user">
            <button
              type="button"
              className="pwa-launcher__edit-btn"
              onClick={() => setEditing((v) => !v)}
              data-testid="pwa-launcher-edit"
              aria-label={editing ? "Done editing" : "Edit dock"}
            >
              {editing ? <><Check size={14} /> Done</> : <><Pencil size={14} /> Edit</>}
            </button>
            {checking ? null : user ? (
              <Link
                to="/account"
                className="pwa-launcher__user-avatar"
                aria-label="Open account"
                data-testid="pwa-launcher-avatar"
              >
                {initial}
              </Link>
            ) : (
              <Link
                to="/login"
                className="pwa-launcher__user-btn"
                data-testid="pwa-launcher-signin"
              >
                Sign in
              </Link>
            )}
          </div>
        </header>

        <section className="pwa-launcher__hero">
          <h1>All your Suite apps, one tap away</h1>
          <p>
            Every HustleSuite module in one place. Pin the four you use
            most to the dock at the bottom — just like your phone.
          </p>
        </section>

        <div style={{ position: "relative", maxWidth: 480 }}>
          <input
            className="pwa-launcher__search"
            type="search"
            placeholder="Search modules…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="pwa-launcher-search"
          />
          <Search
            size={18}
            style={{
              position: "absolute", right: 16, top: "50%",
              transform: "translateY(-50%)", color: "#64748b",
              pointerEvents: "none",
            }}
          />
        </div>

        {editing && (
          <div className="pwa-launcher__edit-hint" data-testid="pwa-launcher-edit-hint">
            Tap any app to pin it to your dock (up to {DOCK_MAX}). Tap a
            docked app to remove it. <button
              type="button"
              className="pwa-launcher__reset"
              onClick={resetDock}
              data-testid="pwa-launcher-reset"
            >Reset to defaults</button>
          </div>
        )}

        {!editing && recentTiles.length > 0 && (
          <section
            className="pwa-launcher__recent"
            data-testid="pwa-launcher-recent"
            aria-label="Recently used"
          >
            <h2 className="pwa-launcher__section-title">
              <Clock size={14} strokeWidth={2} /> Recently used
            </h2>
            <div className="pwa-launcher__grid pwa-launcher__grid--recent">
              {recentTiles.map((tile) => (
                <Tile
                  key={`recent-${tile.key}`}
                  tile={tile}
                  editing={false}
                  inDock={false}
                  dockFull={dockFull}
                  onAdd={addToDock}
                  onClickCapture={trackOpen}
                />
              ))}
            </div>
          </section>
        )}

        <main className="pwa-launcher__grid" data-testid="pwa-launcher-grid">
          {gridTiles.length === 0 ? (
            <div className="pwa-launcher__empty">
              No modules match "{q}". Try a shorter search.
            </div>
          ) : (
            gridTiles.map((tile) => (
              <Tile
                key={tile.key}
                tile={tile}
                editing={editing}
                inDock={false}
                dockFull={dockFull}
                onAdd={addToDock}
                onClickCapture={trackOpen}
              />
            ))
          )}
        </main>

        <footer className="pwa-launcher__footer">
          <div>
            <a href="https://hustlesuite.au">HustleSuite home</a>
            <a href="https://hustlesuite.au/legal/privacy">Privacy</a>
            <a href="https://hustlesuite.au/legal/terms">Terms</a>
            <a href="https://hustlesuite.au/legal/gst">GST</a>
          </div>
          <div className="pwa-launcher__install-hint">
            iOS: Share → Add to Home Screen · Android: ⋮ → Install app
          </div>
        </footer>
      </div>

      {/* iPhone-style bottom dock — always pinned to the viewport. */}
      <nav
        className="pwa-dock"
        data-testid="pwa-dock"
        aria-label="Favourite modules"
      >
        <div className="pwa-dock__inner">
          {Array.from({ length: DOCK_MAX }).map((_, i) => {
            const tile = dockTiles[i];
            if (!tile) {
              return (
                <div
                  key={`slot-${i}`}
                  className="pwa-dock__slot pwa-dock__slot--empty"
                  data-testid={`pwa-dock-slot-${i}`}
                >
                  <div className="pwa-tile__icon pwa-tile__icon--placeholder">
                    <Plus size={22} strokeWidth={2.2} color="#64748b" />
                  </div>
                  <div className="pwa-tile__label">Pin app</div>
                </div>
              );
            }
            return (
              <div key={tile.key} className="pwa-dock__slot">
                <Tile
                  tile={tile}
                  editing={editing}
                  inDock={true}
                  dockFull={dockFull}
                  onRemove={removeFromDock}
                />
              </div>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
